import { useEffect, useRef, useState } from 'react';

type DeferredLoadingOptions = {
  /** Delay before showing loading UI on refetches (when content already exists). */
  delayMs?: number;
  /** Minimum time loading UI stays visible once shown. */
  minVisibleMs?: number;
};

/**
 * Shows loading immediately on first load, but defers it on refetches to avoid flashes.
 */
export function useDeferredLoading(
  active: boolean,
  hasContent: boolean,
  { delayMs = 180, minVisibleMs = 320 }: DeferredLoadingOptions = {},
): boolean {
  const [visible, setVisible] = useState(() => active && !hasContent);
  const shownAtRef = useRef<number | null>(active && !hasContent ? Date.now() : null);
  const delayRef = useRef<number | undefined>(undefined);
  const hideRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    window.clearTimeout(delayRef.current);
    window.clearTimeout(hideRef.current);

    if (active) {
      if (!hasContent) {
        if (!visible) {
          shownAtRef.current = Date.now();
          setVisible(true);
        }
        return undefined;
      }

      delayRef.current = window.setTimeout(() => {
        shownAtRef.current = Date.now();
        setVisible(true);
      }, delayMs);

      return () => window.clearTimeout(delayRef.current);
    }

    if (!visible && !shownAtRef.current) {
      return undefined;
    }

    const elapsed = shownAtRef.current ? Date.now() - shownAtRef.current : minVisibleMs;
    const wait = Math.max(0, minVisibleMs - elapsed);

    hideRef.current = window.setTimeout(() => {
      shownAtRef.current = null;
      setVisible(false);
    }, wait);

    return () => window.clearTimeout(hideRef.current);
  }, [active, hasContent, delayMs, minVisibleMs, visible]);

  return visible;
}
